(() => {
  if (window.StudioDanzaPaymentInitialized) return;
  window.StudioDanzaPaymentInitialized = true;

  const APP_PROXY = "/apps/studio-danza";
  const messageFor = (target, message, state = "") => {
    if (!target) return;
    target.textContent = message;
    target.dataset.state = state;
  };
  const makeRequestId = () =>
    typeof crypto?.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const safeDownloadUrl = (value) => {
    if (typeof value !== "string" || !value.trim()) return "";
    try {
      const url = new URL(value);
      return ["http:", "https:"].includes(url.protocol) ? url.href : "";
    } catch {
      return "";
    }
  };
  const safeSameOriginUrl = (value) => {
    if (typeof value !== "string" || !value.trim()) return "";
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url.href : "";
    } catch {
      return "";
    }
  };
  const formatDate = (date) => {
    const [year, month, day] = String(date).slice(0, 10).split("-");
    return year && month && day ? `${day}/${month}/${year}` : String(date);
  };
  const placeholderNameParts = new Set([
    "cliente",
    "cliente de",
    "de shopify",
    "shopify",
    "cliente shopify",
    "cliente de shopify",
  ]);
  const normalizeNamePart = (value) => typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
  const isRealNamePart = (value) => {
    const name = normalizeNamePart(value);
    return Boolean(name && !placeholderNameParts.has(name.toLocaleLowerCase("es-MX")));
  };
  const normalizedNames = (value) => {
    const firstName = isRealNamePart(value?.firstName) ? normalizeNamePart(value.firstName) : "";
    const lastName = isRealNamePart(value?.lastName) ? normalizeNamePart(value.lastName) : "";
    return { firstName, lastName, complete: Boolean(firstName && lastName) };
  };

  function init(root) {
    if (root.dataset.studioDanzaInitialized === "true") return;
    root.dataset.studioDanzaInitialized = "true";

    const openingSelect = root.querySelector('[id^="studio-opening-"]');
    const openingProperty = root.querySelector('[id^="studio-opening-property-"]');
    const productForm = root.querySelector('form[data-type="add-to-cart-form"]');
    const cartAddUrl = root.dataset.cartAddUrl || "/cart/add.js";
    const studentSpaceUrl =
      safeSameOriginUrl(root.dataset.studentSpaceUrl) ||
      new URL("/pages/mis-cursos", window.location.origin).href;
    const variantInput = root.querySelector('input[ref="variantId"], input[name="id"]');
    const openingMessage = root.querySelector('[id^="studio-opening-message-"]');
    const speiButton = root.querySelector('[id^="bank-transfer-button-"]');
    const speiModal = root.querySelector('[id^="bank-transfer-modal-"]');
    const speiForm = root.querySelector('[id^="bank-transfer-form-"]');
    const speiMessage = root.querySelector(".bank-transfer-message");
    const cashButton = root.querySelector('[id^="cash-payment-button-"]');
    const cashModal = root.querySelector('[id^="cash-payment-modal-"]');
    const cashTerms = root.querySelector(".cash-terms");
    const cashSubmit = root.querySelector(".cash-submit");
    const cashDownload = root.querySelector(".cash-download");
    const cashMessage = root.querySelector(".cash-message");
    const paymentNameGroups = Array.from(root.querySelectorAll("[data-payment-name-fields]"));
    if (!variantInput) return;

    let courseType = root.dataset.courseType || "DATE_RANGE";
    const catalogType = root.dataset.catalogType || "";
    let acceleratedCheckoutInFlight = false;
    let cashRequestInFlight = false;
    let cashReservationCreated = false;
    const cashRequestId = makeRequestId();
    const requiresOpening = () => catalogType === "COURSE" && courseType === "DATE_RANGE";
    let speiAccountLoaded = false;
    let paymentNameLoaded = false;
    let paymentNamePromise = null;
    let paymentName = null;
    let paymentNameDefaults = null;

    function renderPaymentNameFields(names, needsCapture) {
      paymentNameGroups.forEach((group) => {
        const firstName = group.querySelector("[data-payment-first-name]");
        const lastName = group.querySelector("[data-payment-last-name]");
        const status = group.querySelector("[data-payment-name-status]");
        group.hidden = !needsCapture;
        [firstName, lastName].forEach((field) => {
          if (!field) return;
          field.required = needsCapture;
          field.disabled = !needsCapture;
          field.value = names[field.dataset.paymentFirstName !== undefined ? "firstName" : "lastName"] || "";
          field.removeAttribute("aria-invalid");
        });
        messageFor(status, needsCapture ? "Completa los dos campos obligatorios para continuar." : "");
      });
    }

    async function loadPaymentName() {
      if (paymentNameLoaded) return paymentName;
      if (paymentNamePromise) return paymentNamePromise;
      paymentNamePromise = (async () => {
        const shopifyNames = normalizedNames({ firstName: root.dataset.shopifyFirstName, lastName: root.dataset.shopifyLastName });
        let studioNames = normalizedNames(null);
        try {
          const response = await fetch(`${APP_PROXY}/perfil`, { credentials: "same-origin", headers: { Accept: "application/json" } });
          if (response.ok) {
            const payload = await response.json().catch(() => ({}));
            studioNames = normalizedNames(payload?.profile);
          }
        } catch {
          // Shopify's authenticated Liquid profile remains a usable fallback.
        }
        paymentName = studioNames.complete ? studioNames : shopifyNames.complete ? shopifyNames : null;
        paymentNameDefaults = paymentName || {
          firstName: studioNames.firstName || shopifyNames.firstName,
          lastName: studioNames.lastName || shopifyNames.lastName,
        };
        paymentNameLoaded = true;
        renderPaymentNameFields(paymentNameDefaults, !paymentName);
        return paymentName;
      })();
      return paymentNamePromise;
    }

    function paymentNameFor(dialog, statusMessage) {
      if (!paymentNameLoaded) {
        messageFor(statusMessage, "Estamos verificando tu nombre. Inténtalo de nuevo en un momento.", "error");
        return null;
      }
      if (paymentName) return paymentName;
      const group = dialog?.querySelector("[data-payment-name-fields]");
      const firstNameField = group?.querySelector("[data-payment-first-name]");
      const lastNameField = group?.querySelector("[data-payment-last-name]");
      const firstName = normalizeNamePart(firstNameField?.value);
      const lastName = normalizeNamePart(lastNameField?.value);
      const firstNameValid = isRealNamePart(firstName);
      const lastNameValid = isRealNamePart(lastName);
      if (firstNameField) {
        if (firstNameValid) firstNameField.removeAttribute("aria-invalid");
        else firstNameField.setAttribute("aria-invalid", "true");
      }
      if (lastNameField) {
        if (lastNameValid) lastNameField.removeAttribute("aria-invalid");
        else lastNameField.setAttribute("aria-invalid", "true");
      }
      if (!firstNameValid || !lastNameValid) {
        const nameStatus = group?.querySelector("[data-payment-name-status]");
        const message = !firstNameValid && !lastNameValid
          ? "Escribe tu nombre y apellido para continuar."
          : !firstNameValid
            ? "Escribe un nombre real para continuar."
            : "Escribe un apellido real para continuar.";
        messageFor(nameStatus, message, "error");
        messageFor(statusMessage, message, "error");
        (firstNameValid ? lastNameField : firstNameField)?.focus();
        return null;
      }
      messageFor(group?.querySelector("[data-payment-name-status]"), "");
      return { firstName, lastName };
    }

    paymentNameGroups.forEach((group) => group.addEventListener("input", (event) => {
      const field = event.target.closest("[data-payment-first-name], [data-payment-last-name]");
      if (!field) return;
      if (isRealNamePart(field.value)) field.removeAttribute("aria-invalid");
      const nameStatus = group.querySelector("[data-payment-name-status]");
      if (nameStatus?.dataset.state === "error") messageFor(nameStatus, "Completa los dos campos obligatorios para continuar.");
    }));

    const syncPaymentDialogToVisualViewport = () => {
      const viewport = window.visualViewport;
      if (!viewport) return;
      const keyboardOrZoomViewport = viewport.height < window.innerHeight - 80 || viewport.offsetTop > 0;
      [speiModal, cashModal].forEach((dialog) => {
        if (!dialog?.open) return;
        if (keyboardOrZoomViewport) {
          dialog.style.top = `${Math.max(8, viewport.offsetTop + 8)}px`;
          dialog.style.bottom = "auto";
          dialog.style.marginBlock = "0";
          dialog.style.transform = "none";
          dialog.style.maxHeight = `${Math.max(160, viewport.height - 16)}px`;
        } else {
          dialog.style.removeProperty("top");
          dialog.style.removeProperty("bottom");
          dialog.style.removeProperty("margin-block");
          dialog.style.removeProperty("transform");
          dialog.style.removeProperty("max-height");
        }
      });
      keepFocusedPaymentFieldAboveKeyboard();
    };
    const keepFocusedPaymentFieldAboveKeyboard = () => {
      const viewport = window.visualViewport;
      const active = document.activeElement;
      if (!viewport || !(active instanceof HTMLElement) || !active.matches("input, select, textarea")) return;
      const dialog = active.closest(".studio-danza-payment__dialog");
      if (!dialog) return;
      window.requestAnimationFrame(() => {
        const rect = active.getBoundingClientRect();
        const visibleTop = viewport.offsetTop + 12;
        const visibleBottom = viewport.offsetTop + viewport.height - 12;
        if (rect.bottom > visibleBottom) dialog.scrollBy({ top: rect.bottom - visibleBottom, behavior: "auto" });
        else if (rect.top < visibleTop) dialog.scrollBy({ top: rect.top - visibleTop, behavior: "auto" });
      });
    };
    window.visualViewport?.addEventListener("resize", syncPaymentDialogToVisualViewport);
    window.visualViewport?.addEventListener("scroll", syncPaymentDialogToVisualViewport);
    window.addEventListener("resize", syncPaymentDialogToVisualViewport);
    document.addEventListener("focusin", keepFocusedPaymentFieldAboveKeyboard);
    [speiModal, cashModal].forEach((dialog) => dialog?.addEventListener("close", syncPaymentDialogToVisualViewport));

    speiForm?.addEventListener("invalid", (event) => {
      const field = event.target;
      if (!field.matches?.("[data-payment-first-name], [data-payment-last-name]")) return;
      field.setAttribute("aria-invalid", "true");
      const group = field.closest("[data-payment-name-fields]");
      messageFor(group?.querySelector("[data-payment-name-status]"), field.dataset.paymentFirstName !== undefined ? "Escribe tu nombre para continuar." : "Escribe tu apellido para continuar.", "error");
    }, true);

    async function loadSpeiAccount() {
      if (speiAccountLoaded) return true;
      const panel = root.querySelector("[data-spei-account]");
      const status = root.querySelector("[data-spei-account-message]");
      try {
        const response = await fetch(`${APP_PROXY}/spei-account`, { credentials: "same-origin", headers: { Accept: "application/json" } });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "No fue posible consultar la cuenta SPEI.");
        if (!payload.account) throw new Error("No hay una cuenta SPEI activa. Contacta a la academia.");
        root.querySelector("[data-spei-bank]").textContent = payload.account.bankName;
        root.querySelector("[data-spei-beneficiary]").textContent = payload.account.beneficiaryName;
        root.querySelector("[data-spei-clabe]").textContent = payload.account.clabe;
        const cardRow = root.querySelector("[data-spei-card-row]");
        if (payload.account.cardNumber) root.querySelector("[data-spei-card]").textContent = payload.account.cardNumber;
        else if (cardRow) cardRow.hidden = true;
        if (panel) panel.hidden = false;
        if (status) status.hidden = true;
        speiAccountLoaded = true;
        return true;
      } catch (error) {
        messageFor(status, error instanceof Error ? error.message : "No fue posible consultar la cuenta SPEI.", "error");
        return false;
      }
    }

    let loadedVariantId = "";
    const variantId = () => {
      const id = String(variantInput.value || "").trim();
      return id.startsWith("gid://shopify/ProductVariant/")
        ? id
        : `gid://shopify/ProductVariant/${id}`;
    };
    const selectedOpening = () => openingSelect?.value.trim() || "";
    const requireOpening = () => {
      if (!requiresOpening()) return true;
      if (selectedOpening()) return true;
      messageFor(openingMessage, "Elige una fecha de curso para continuar.", "error");
      openingSelect?.focus();
      return false;
    };
    const close = (dialog) => {
      if (dialog?.open) dialog.close();
    };
    const open = (dialog) => {
      if (dialog && !dialog.open) dialog.showModal();
    };

    async function addSelectedProductAndCheckout() {
      if (!window.StudioDanzaPaymentPolicy?.allowed("native")) return;
      if (acceleratedCheckoutInFlight || !productForm || !requiresOpening()) return;
      if (!requireOpening()) return;
      // Keep the property synchronized even if a theme has replaced the
      // select/input nodes during a variant section refresh.
      if (openingProperty) openingProperty.value = selectedOpening();
      acceleratedCheckoutInFlight = true;
      messageFor(openingMessage, "Preparando tu checkout…");
      try {
        const response = await fetch(cartAddUrl, {
          method: "POST",
          body: new FormData(productForm),
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || "No fue posible preparar el checkout.");
        }
        const shopifyRoot = window.Shopify?.routes?.root || "/";
        window.location.assign(`${shopifyRoot.replace(/\/?$/, "/")}checkout`);
      } catch (error) {
        messageFor(openingMessage, error instanceof Error ? error.message : "No fue posible preparar el checkout.", "error");
        acceleratedCheckoutInFlight = false;
      }
    }

    async function loadOpenings() {
      if (!requiresOpening() || !openingSelect || !openingProperty) return;
      const currentVariantId = variantId();
      if (!currentVariantId || currentVariantId.endsWith("/")) return;
      loadedVariantId = currentVariantId;
      openingProperty.value = "";
      openingSelect.disabled = true;
      openingSelect.replaceChildren(new Option("Cargando fechas…", ""));
      messageFor(openingMessage, "");
      try {
        const response = await fetch(
          `${APP_PROXY}/openings?variantId=${encodeURIComponent(currentVariantId)}`,
          { credentials: "same-origin", headers: { Accept: "application/json" } },
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "No fue posible cargar las fechas.");
        if (payload.courseType === "DATE_RANGE" || payload.courseType === "FLEXIBLE_START") {
          courseType = payload.courseType;
          root.dataset.courseType = courseType;
          if (!requiresOpening()) {
            openingSelect.closest(".studio-danza-opening-field")?.remove();
            openingProperty.remove();
            return;
          }
        }
        const openings = Array.isArray(payload.openings) ? payload.openings : [];
        openingSelect.replaceChildren(new Option("Elige una fecha", ""));
        openings.forEach(({ id, startsAt, endsAt }) => {
          openingSelect.add(new Option(`${formatDate(startsAt)} al ${formatDate(endsAt)}`, id));
        });
        if (!openings.length) {
          messageFor(openingMessage, "No hay fechas disponibles para esta variante.", "error");
        }
      } catch (error) {
        openingSelect.replaceChildren(new Option("No fue posible cargar fechas", ""));
        messageFor(
          openingMessage,
          error instanceof Error ? error.message : "No fue posible cargar las fechas.",
          "error",
        );
      } finally {
        openingSelect.disabled = false;
      }
    }

    openingSelect?.addEventListener("change", () => {
      if (openingProperty) openingProperty.value = selectedOpening();
      messageFor(openingMessage, "");
    });

    // The property is inside Shopify's real product form, so it accompanies card,
    // add-to-cart and accelerated checkout requests.
    productForm?.addEventListener(
      "submit",
      (event) => {
        if (!requireOpening()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true,
    );

    // "Comprar ahora" is Shopify's accelerated-checkout control. It does not
    // submit the product form first, so native form validation and the submit
    // listener above never get a chance to stop it. Intercept its composed
    // click while it travels through the wrapper, before Shopify opens checkout.
    root.addEventListener(
      "click",
      (event) => {
        const isAcceleratedCheckout = event.composedPath().some(
          (node) => node instanceof Element && node.classList.contains("accelerated-checkout-block"),
        );
        if (isAcceleratedCheckout && requiresOpening()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          // Shopify's accelerated checkout can omit custom line-item
          // properties. Add the product through the cart endpoint first so
          // `course_opening_id` reaches the `orders/paid` webhook reliably.
          void addSelectedProductAndCheckout();
        }
      },
      true,
    );

    // Themes update the hidden variant input after their own variant selector changes.
    root.addEventListener("change", (event) => {
      if (event.target === openingSelect) return;
      window.setTimeout(() => {
        if (variantId() !== loadedVariantId) void loadOpenings();
      }, 0);
    });
    new MutationObserver(() => {
      if (variantId() !== loadedVariantId) void loadOpenings();
    }).observe(variantInput, { attributes: true, attributeFilter: ["value"] });

    speiButton?.addEventListener("click", async () => {
      if (!window.StudioDanzaPaymentPolicy?.allowed("spei")) return;
      if (requireOpening()) {
        open(speiModal);
        syncPaymentDialogToVisualViewport();
        void loadPaymentName();
        await loadSpeiAccount();
      }
    });
    root.querySelectorAll("[data-copy-spei]").forEach((button) => button.addEventListener("click", async () => {
      const target = root.querySelector(button.dataset.copySpei === "card" ? "[data-spei-card]" : "[data-spei-clabe]");
      if (target?.textContent) await navigator.clipboard.writeText(target.textContent.trim());
      const original = button.textContent;
      button.textContent = "Copiado";
      window.setTimeout(() => { button.textContent = original; }, 1200);
    }));
    root.querySelectorAll(".bank-transfer-close").forEach((button) =>
      button.addEventListener("click", () => close(speiModal)),
    );
    speiForm?.addEventListener("submit", async (event) => {
      if (!window.StudioDanzaPaymentPolicy?.allowed("spei")) { event.preventDefault(); return; }
      event.preventDefault();
      if (!requireOpening()) return;
      const submittedName = paymentNameFor(speiModal, speiMessage);
      if (!submittedName) return;
      const proof = speiForm.elements.proof?.files?.[0];
      if (!proof) {
        messageFor(speiMessage, "Selecciona un comprobante.", "error");
        return;
      }
      const submit = speiForm.querySelector('button[type="submit"]');
      if (submit) submit.disabled = true;
      messageFor(speiMessage, "Enviando comprobante…");
      try {
        const body = new FormData();
        body.set("proof", proof);
        body.set("firstName", submittedName.firstName);
        body.set("lastName", submittedName.lastName);
        body.set("variantId", variantId());
        if (selectedOpening()) body.set("openingId", selectedOpening());
        body.set("requestId", makeRequestId());
        const response = await fetch(`${APP_PROXY}/spei`, {
          method: "POST",
          body,
          credentials: "same-origin",
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "No fue posible enviar el comprobante.");
        if (payload.status !== "PENDING_REVIEW") {
          throw new Error(payload.error || "No fue posible confirmar la recepción del comprobante.");
        }
        speiForm.reset();
        close(speiModal);
        messageFor(openingMessage, payload.message || "Comprobante enviado. Revisaremos tu pago.", "success");
        window.location.assign(studentSpaceUrl);
      } catch (error) {
        messageFor(speiMessage, error instanceof Error ? error.message : "No fue posible enviar el comprobante.", "error");
      } finally {
        if (submit) submit.disabled = false;
      }
    });

    cashButton?.addEventListener("click", () => {
      if (!window.StudioDanzaPaymentPolicy?.allowed("cash")) return;
      if (requireOpening()) {
        open(cashModal);
        syncPaymentDialogToVisualViewport();
        void loadPaymentName();
      }
    });
    root.querySelectorAll(".cash-payment-close").forEach((button) =>
      button.addEventListener("click", () => close(cashModal)),
    );
    cashSubmit?.addEventListener("click", async () => {
      if (cashRequestInFlight || cashReservationCreated) return;
      if (!window.StudioDanzaPaymentPolicy?.allowed("cash")) return;
      if (!requireOpening()) return;
      const submittedName = paymentNameFor(cashModal, cashMessage);
      if (!submittedName) return;
      if (!cashTerms?.checked) {
        messageFor(cashMessage, "Debes aceptar los términos y condiciones.", "error");
        return;
      }
      cashRequestInFlight = true;
      cashSubmit.disabled = true;
      messageFor(cashMessage, "Generando comprobante…");
      try {
        const body = new FormData();
        body.set("firstName", submittedName.firstName);
        body.set("lastName", submittedName.lastName);
        body.set("variantId", variantId());
        if (selectedOpening()) body.set("openingId", selectedOpening());
        body.set("termsAccepted", "true");
        body.set("requestId", cashRequestId);
        const response = await fetch(`${APP_PROXY}/cash`, {
          method: "POST",
          body,
          credentials: "same-origin",
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "No fue posible crear la reserva.");
        cashReservationCreated = true;
        const downloadUrl = safeDownloadUrl(payload.downloadUrl);
        if (downloadUrl && cashDownload) {
          cashDownload.href = downloadUrl;
          cashDownload.hidden = false;
          cashSubmit.hidden = true;
          messageFor(cashMessage, payload.message || "Tu clase quedó reservada.", "success");
          cashDownload.focus();
        } else {
          cashSubmit.textContent = "Reserva registrada";
          messageFor(cashMessage, `${payload.message || "Tu clase quedó reservada."} No recibimos el enlace de descarga. Consulta tu reserva en Mi espacio; no necesitas generarla de nuevo.`, "success");
        }
      } catch (error) {
        messageFor(cashMessage, error instanceof Error ? error.message : "No fue posible crear la reserva.", "error");
      } finally {
        cashRequestInFlight = false;
        if (!cashReservationCreated) cashSubmit.disabled = false;
      }
    });

    if (requiresOpening()) void loadOpenings();
  }

  const initAll = () => document.querySelectorAll(".studio-danza-payment").forEach(init);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", initAll);
})();
